import React, { useState, useEffect } from 'react';
import { 
  X, 
  Play, 
  Bug, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  RotateCcw,
  Download,
  Eye,
  Settings,
  Zap
} from 'lucide-react';
import { Workflow, WorkflowExecution, ExecutionStep } from '../types/workflow';
import { workflowService } from '../services/workflowService';

interface WorkflowTestPanelProps {
  workflow: Workflow;
  onClose: () => void;
}

export const WorkflowTestPanel: React.FC<WorkflowTestPanelProps> = ({
  workflow,
  onClose
}) => {
  const [testData, setTestData] = useState<Record<string, any>>({});
  const [execution, setExecution] = useState<WorkflowExecution | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState<'input' | 'execution' | 'results'>('input');
  const [executionHistory, setExecutionHistory] = useState<WorkflowExecution[]>([]);

  useEffect(() => {
    loadExecutionHistory();
  }, [workflow.id]);

  const loadExecutionHistory = async () => {
    try {
      const executions = await workflowService.getExecutions(workflow.id, 1, 10);
      setExecutionHistory(executions);
    } catch (error) {
      console.error('Failed to load execution history:', error);
    }
  };

  const handleRunTest = async () => {
    try {
      setIsRunning(true);
      setActiveTab('execution');
      
      const response = await workflowService.testWorkflow(workflow.id, testData);
      
      // Subscribe to execution updates
      const unsubscribe = workflowService.subscribeToExecution(response.executionId, (event) => {
        if (event.type === 'execution_completed' || event.type === 'execution_failed') {
          setIsRunning(false);
          loadExecutionDetails(response.executionId);
          unsubscribe();
        }
      });

      // Load initial execution details
      setTimeout(() => {
        loadExecutionDetails(response.executionId);
      }, 1000);

    } catch (error) {
      console.error('Failed to run test:', error);
      setIsRunning(false);
    }
  };

  const loadExecutionDetails = async (executionId: string) => {
    try {
      const executionDetails = await workflowService.getExecution(executionId);
      setExecution(executionDetails);
      setActiveTab('results');
    } catch (error) {
      console.error('Failed to load execution details:', error);
    }
  };

  const getStepStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'running':
        return <Clock className="w-4 h-4 text-blue-400 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-neutral-400" />;
    }
  };

  const getStepStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'border-green-400 bg-green-400/10';
      case 'failed':
        return 'border-red-400 bg-red-400/10';
      case 'running':
        return 'border-blue-400 bg-blue-400/10';
      default:
        return 'border-neutral-600 bg-neutral-800/50';
    }
  };

  const renderInputTab = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Test Input Data</h3>
        <p className="text-neutral-400 text-sm mb-4">
          Provide test data that will be used as input for the workflow execution.
        </p>
      </div>

      {/* Workflow Variables */}
      {workflow.definition.variables && workflow.definition.variables.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-white mb-3">Workflow Variables</h4>
          <div className="space-y-3">
            {workflow.definition.variables.map((variable, index) => (
              <div key={index}>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  {variable.name}
                  <span className="text-neutral-400 ml-2">({variable.type})</span>
                </label>
                <input
                  type={variable.type === 'number' ? 'number' : 'text'}
                  value={testData[variable.name] || variable.defaultValue || ''}
                  onChange={(e) => setTestData(prev => ({
                    ...prev,
                    [variable.name]: variable.type === 'number' ? parseFloat(e.target.value) : e.target.value
                  }))}
                  className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={variable.description || `Enter ${variable.name}`}
                />
                {variable.description && (
                  <p className="text-xs text-neutral-400 mt-1">{variable.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom Test Data */}
      <div>
        <h4 className="text-md font-medium text-white mb-3">Custom Test Data (JSON)</h4>
        <textarea
          value={JSON.stringify(testData, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              setTestData(parsed);
            } catch {
              // Invalid JSON, ignore
            }
          }}
          rows={8}
          className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder='{\n  "key": "value",\n  "number": 123\n}'
        />
        <p className="text-xs text-neutral-400 mt-1">
          Provide additional test data in JSON format
        </p>
      </div>

      {/* Test Actions */}
      <div className="flex items-center gap-3 pt-4">
        <button
          onClick={handleRunTest}
          disabled={isRunning}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-neutral-700 disabled:text-neutral-400 text-white px-6 py-3 rounded-lg transition-colors"
        >
          {isRunning ? (
            <>
              <Clock className="w-4 h-4 animate-spin" />
              Running Test...
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Run Test
            </>
          )}
        </button>
        
        <button
          onClick={() => setTestData({})}
          className="flex items-center gap-2 bg-neutral-700 hover:bg-neutral-600 text-white px-4 py-3 rounded-lg transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </button>
      </div>
    </div>
  );

  const renderExecutionTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">Test Execution</h3>
        {execution && (
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            execution.status === 'completed' ? 'bg-green-400/20 text-green-400' :
            execution.status === 'failed' ? 'bg-red-400/20 text-red-400' :
            execution.status === 'running' ? 'bg-blue-400/20 text-blue-400' :
            'bg-neutral-400/20 text-neutral-400'
          }`}>
            {execution.status.toUpperCase()}
          </div>
        )}
      </div>

      {execution ? (
        <div className="space-y-4">
          {/* Execution Info */}
          <div className="bg-neutral-800/50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-neutral-400">Started:</span>
                <span className="text-white ml-2">
                  {new Date(execution.startedAt).toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-neutral-400">Duration:</span>
                <span className="text-white ml-2">
                  {execution.duration ? `${(execution.duration / 1000).toFixed(2)}s` : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Step Progress */}
          <div className="space-y-3">
            <h4 className="text-md font-medium text-white">Step Progress</h4>
            {execution.steps.map((step, index) => {
              const workflowStep = workflow.definition.steps.find(s => s.id === step.stepId);
              return (
                <div
                  key={step.stepId}
                  className={`p-4 rounded-lg border-2 ${getStepStatusColor(step.status)}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      {getStepStatusIcon(step.status)}
                      <span className="text-white font-medium">
                        {workflowStep?.name || `Step ${index + 1}`}
                      </span>
                    </div>
                    <div className="text-sm text-neutral-400">
                      {step.duration ? `${(step.duration / 1000).toFixed(2)}s` : ''}
                    </div>
                  </div>
                  
                  {step.error && (
                    <div className="mt-2 p-2 bg-red-900/20 border border-red-800 rounded text-red-300 text-sm">
                      <strong>Error:</strong> {step.error.message}
                    </div>
                  )}
                  
                  {step.output && Object.keys(step.output).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-sm text-neutral-400 cursor-pointer hover:text-white">
                        View Output
                      </summary>
                      <pre className="mt-2 p-2 bg-neutral-900 rounded text-xs text-neutral-300 overflow-x-auto">
                        {JSON.stringify(step.output, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-4">
            {isRunning ? (
              <Clock className="w-8 h-8 text-blue-400 animate-spin" />
            ) : (
              <Bug className="w-8 h-8 text-neutral-400" />
            )}
          </div>
          <h3 className="text-lg font-medium text-white mb-2">
            {isRunning ? 'Test Running...' : 'No Test Execution'}
          </h3>
          <p className="text-neutral-400">
            {isRunning ? 'Please wait while the test is running' : 'Run a test to see execution details'}
          </p>
        </div>
      )}
    </div>
  );

  const renderResultsTab = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-white">Test Results</h3>
      
      {execution ? (
        <div className="space-y-4">
          {/* Summary */}
          <div className="bg-neutral-800/50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className={`text-2xl font-bold ${
                  execution.status === 'completed' ? 'text-green-400' :
                  execution.status === 'failed' ? 'text-red-400' :
                  'text-neutral-400'
                }`}>
                  {execution.status === 'completed' ? '✓' : 
                   execution.status === 'failed' ? '✗' : '○'}
                </div>
                <div className="text-sm text-neutral-400 mt-1">Status</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">
                  {execution.duration ? `${(execution.duration / 1000).toFixed(2)}s` : 'N/A'}
                </div>
                <div className="text-sm text-neutral-400 mt-1">Duration</div>
              </div>
            </div>
          </div>

          {/* Final Output */}
          {execution.context.stepResults && Object.keys(execution.context.stepResults).length > 0 && (
            <div>
              <h4 className="text-md font-medium text-white mb-3">Final Output</h4>
              <div className="bg-neutral-900 rounded-lg p-4">
                <pre className="text-sm text-neutral-300 overflow-x-auto">
                  {JSON.stringify(execution.context.stepResults, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Error Details */}
          {execution.error && (
            <div>
              <h4 className="text-md font-medium text-white mb-3">Error Details</h4>
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
                <div className="text-red-300">
                  <strong>Code:</strong> {execution.error.code}
                </div>
                <div className="text-red-300 mt-2">
                  <strong>Message:</strong> {execution.error.message}
                </div>
                {execution.error.details && (
                  <details className="mt-3">
                    <summary className="text-red-400 cursor-pointer">View Details</summary>
                    <pre className="mt-2 text-xs text-red-200 overflow-x-auto">
                      {JSON.stringify(execution.error.details, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleRunTest}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Run Again
            </button>
            
            <button
              onClick={() => {
                const dataStr = JSON.stringify(execution, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `test-results-${execution.id}.json`;
                link.click();
                URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-2 bg-neutral-700 hover:bg-neutral-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Export Results
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Eye className="w-8 h-8 text-neutral-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No Results Yet</h3>
          <p className="text-neutral-400">Run a test to see the results here</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-neutral-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Bug className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Test Workflow</h2>
                <p className="text-neutral-400 text-sm">{workflow.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-neutral-800 rounded-lg p-1 mt-4">
            {[
              { id: 'input', label: 'Input', icon: Settings },
              { id: 'execution', label: 'Execution', icon: Zap },
              { id: 'results', label: 'Results', icon: Eye }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id as any)}
                className={`flex items-center gap-2 flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === id
                    ? 'bg-blue-600 text-white'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {activeTab === 'input' && renderInputTab()}
          {activeTab === 'execution' && renderExecutionTab()}
          {activeTab === 'results' && renderResultsTab()}
        </div>
      </div>
    </div>
  );
}; 